import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useCatalogo, type TreinamentoCatalogo } from '@/lib/catalogo';
import { dataValida, hojeISO, MENSAGEM_DATA, noFuturo } from '@/lib/datas';

// Registrar treinamento a partir da tela de treinamentos.
//
// Antes só dava para registrar entrando na ficha do funcionário, que é onde a
// pessoa está quando cadastra alguém — mas não quando volta de um curso com
// cinco certificados na mão. Esta é a tela onde ela está nesse momento.
//
// A lista de treinamentos oferecida é a que as profissões do funcionário
// exigem; sem profissão cadastrada não há exigência a registrar, e o modal diz
// isso em vez de oferecer o catálogo inteiro.

interface FuncionarioOpcao {
  id: string;
  nome: string;
  funcao: string | null;
  profissoes_ids: string[] | null;
  condominio: string;
}

interface Props {
  onFechar: () => void;
  onRegistrado: () => void;
}

const CLASSE_CAMPO =
  'w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm';

export default function RegistrarTreinamento({ onFechar, onRegistrado }: Props) {
  const { effectiveAccountId } = useAuth();
  const { itens: catalogo } = useCatalogo<TreinamentoCatalogo>('treinamentos');

  const [funcionarios, setFuncionarios] = useState<FuncionarioOpcao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [funcionarioId, setFuncionarioId] = useState('');
  const [treinamentoId, setTreinamentoId] = useState('');
  const [dataConclusao, setDataConclusao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let cancelado = false;

    Promise.resolve(
      supabase
        .from('funcionarios')
        .select('id, nome, funcao, profissoes_ids, condominios(nome)')
        .order('nome'),
    )
      .then(({ data, error }) => {
        if (cancelado) return;
        if (error) {
          setErro(error.message);
          setCarregando(false);
          return;
        }
        setFuncionarios(
          (data ?? []).map((linha: Record<string, any>) => ({
            id: linha.id,
            nome: linha.nome,
            funcao: linha.funcao,
            profissoes_ids: linha.profissoes_ids,
            condominio: linha.condominios?.nome ?? '',
          })),
        );
        setCarregando(false);
      })
      .catch(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
  }, []);

  const funcionario = funcionarios.find((f) => f.id === funcionarioId);

  const exigidos = useMemo(() => {
    if (!funcionario) return [];
    const profissoes = funcionario.profissoes_ids ?? [];
    // N:N desde a migration 0047, mais os que valem para qualquer profissão —
    // integração de segurança, EPI. Antes eles não apareciam na lista de
    // exigidos, porque não havia como um treinamento pertencer a todas.
    return catalogo.filter(
      (t) => t.aplica_a_qualquer_profissao || t.profissoes.some((p) => profissoes.includes(p)),
    );
  }, [catalogo, funcionario]);

  // Trocar de funcionário zera a escolha: o treinamento anterior pode não ser
  // exigido pelas profissões do novo.
  useEffect(() => {
    setTreinamentoId('');
  }, [funcionarioId]);

  const registrar = async () => {
    setErro(null);

    if (!funcionarioId || !treinamentoId) {
      setErro('Escolha o funcionário e o treinamento.');
      return;
    }
    if (!dataValida(dataConclusao)) {
      setErro(MENSAGEM_DATA.invalida);
      return;
    }
    if (noFuturo(dataConclusao)) {
      setErro('A conclusão do treinamento não pode ser uma data futura.');
      return;
    }

    const doCatalogo = catalogo.find((t) => t.id === treinamentoId);

    setSalvando(true);

    // Lê antes de escrever, em vez de `upsert`: o índice único é **parcial**
    // (`where deletado_em is null`), e o Postgres não infere `ON CONFLICT` a
    // partir de índice parcial — a chamada volta com "no unique or exclusion
    // constraint matching the ON CONFLICT specification".
    const { data: existente } = await supabase
      .from('funcionarios_treinamentos')
      .select('id')
      .eq('funcionario_id', funcionarioId)
      .eq('treinamento_id', treinamentoId)
      .maybeSingle();

    const registro = {
      account_id: effectiveAccountId,
      funcionario_id: funcionarioId,
      treinamento_id: treinamentoId,
      data_conclusao: dataConclusao,
      vigencia_dias: doCatalogo?.vigencia_dias ?? 365,
      editado_em: new Date().toISOString(),
    };

    // Reciclagem atualiza o registro que existe; não cria um segundo em paralelo.
    const { error } = existente
      ? await supabase.from('funcionarios_treinamentos').update(registro).eq('id', existente.id)
      : await supabase.from('funcionarios_treinamentos').insert(registro);

    setSalvando(false);

    if (error) {
      setErro(error.message);
      return;
    }

    onRegistrado();
    onFechar();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onFechar}></div>

      <div className="relative bg-background-50 rounded-lg border border-background-200/70 shadow-lg w-full max-w-lg">
        <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground-900">Registrar treinamento</h3>
          <button
            onClick={onFechar}
            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100"
            aria-label="Fechar"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4">
          {erro && (
            <div className="px-3 py-2.5 rounded-md border border-red-200 bg-red-50 text-sm text-red-700">
              {erro}
            </div>
          )}

          <div>
            <label
              htmlFor="treino_funcionario"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Funcionário *
            </label>
            <select
              id="treino_funcionario"
              value={funcionarioId}
              disabled={carregando}
              onChange={(e) => setFuncionarioId(e.target.value)}
              className={CLASSE_CAMPO}
            >
              <option value="">{carregando ? 'Carregando...' : 'Selecione...'}</option>
              {funcionarios.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome}
                  {f.funcao ? ` — ${f.funcao}` : ''}
                  {f.condominio ? ` (${f.condominio})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="treino_catalogo"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Treinamento *
            </label>
            <select
              id="treino_catalogo"
              value={treinamentoId}
              disabled={!funcionario || exigidos.length === 0}
              onChange={(e) => setTreinamentoId(e.target.value)}
              className={CLASSE_CAMPO}
            >
              <option value="">Selecione...</option>
              {exigidos.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}
                  {t.norma ? ` — ${t.norma}` : ''}
                </option>
              ))}
            </select>

            {funcionario && exigidos.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                {funcionario.nome} não tem função cadastrada, então nenhum treinamento é exigido.
                Cadastre a função na ficha do funcionário.
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="treino_conclusao"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Data de conclusão *
            </label>
            <input
              id="treino_conclusao"
              type="date"
              value={dataConclusao}
              max={hojeISO()}
              onChange={(e) => setDataConclusao(e.target.value)}
              className={CLASSE_CAMPO}
            />
            <p className="text-xs text-foreground-500 mt-1">
              O vencimento é calculado pelo banco a partir da vigência da norma.
            </p>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-background-200/70 flex justify-end gap-3">
          <button
            onClick={onFechar}
            className="px-4 py-2.5 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={registrar}
            disabled={salvando || !funcionarioId || !treinamentoId || !dataConclusao}
            className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {salvando ? 'Registrando...' : 'Registrar'}
          </button>
        </div>
      </div>
    </div>
  );
}
